import React, { useMemo } from 'react';
import { Box, Stack, Typography } from '@mui/material';
import { brandColors } from '@config/brandTokens';
import FoundationScoutPanel, { type FoundationBlock } from '@components/profile/FoundationScoutPanel';
import SkillQuarterJourney, { type SkillJourneyItem } from '@components/profile/SkillQuarterJourney';

export type ContestSportStat = {
  sport: string;
  totalScore: number;
  avgScore: number | null;
  submissionCount: number;
  improvement?: number | null;
};

export type ImprovementBlock = {
  currentAvg?: number | null;
  previousAvg?: number | null;
  delta?: number | null;
  trend?: Array<{ key: string; label: string; avgScore: number | null }>;
  sports?: Array<{
    sport: string;
    avgScore: number | null;
    totalScore: number;
    submissionCount: number;
    improvement: number | null;
  }>;
};

export type RecentSkill = {
  skillName?: string;
  score?: number | null;
  ageCategory?: string;
  quarter?: string | null;
};

const railCard = {
  border: '1px solid #e2e8f0',
  borderRadius: 1.25,
  bgcolor: '#fff',
};

function groupJourneys(journeys: SkillJourneyItem[]) {
  const groups: Array<{ sport: string; items: SkillJourneyItem[] }> = [];
  journeys.forEach((journey) => {
    const sport = journey.sport || 'Sport';
    const existing = groups.find((group) => group.sport.toLowerCase() === sport.toLowerCase());
    if (existing) existing.items.push(journey);
    else groups.push({ sport, items: [journey] });
  });
  return groups;
}

const PublicSportsPerformancePanel: React.FC<{
  player: {
    firstName?: string;
    lastName?: string;
    playerIdCode?: string;
    id?: string;
  };
  contestBySport?: ContestSportStat[];
  foundation?: FoundationBlock | null;
  foundationBySport?: FoundationBlock[];
  skillJourneys?: SkillJourneyItem[];
  allSkillJourneys?: SkillJourneyItem[];
  improvement?: ImprovementBlock | null;
  recentSkills?: RecentSkill[];
}> = ({
  player,
  contestBySport,
  foundation,
  foundationBySport,
  skillJourneys,
  allSkillJourneys,
  improvement,
  recentSkills,
}) => {
  const scoreRows = useMemo(() => {
    const fromImprovement = improvement?.sports || [];
    const rows = fromImprovement.length
      ? fromImprovement
      : (contestBySport || []).map((row) => ({
          sport: row.sport,
          avgScore: row.avgScore,
          totalScore: row.totalScore,
          submissionCount: row.submissionCount,
          improvement: row.improvement ?? null,
        }));
    return rows.filter((row) => row.submissionCount > 0 || row.avgScore != null);
  }, [contestBySport, improvement?.sports]);

  const foundations = useMemo(() => {
    if (foundationBySport?.length) return foundationBySport;
    return foundation ? [foundation] : [];
  }, [foundation, foundationBySport]);

  const journeyGroups = useMemo(
    () => groupJourneys(allSkillJourneys?.length ? allSkillJourneys : skillJourneys || []),
    [allSkillJourneys, skillJourneys]
  );

  const trend = improvement?.trend || [];
  const hasScores = scoreRows.some((row) => row.submissionCount > 0 || row.avgScore != null);
  const hasAnything =
    hasScores || foundations.length > 0 || journeyGroups.length > 0 || Boolean(recentSkills?.length);

  if (!hasAnything) {
    return (
      <Box sx={{ ...railCard, px: 1.75, py: 2 }}>
        <Typography sx={{ fontWeight: 800, color: brandColors.navy, fontFamily: '"Sora", Arial, sans-serif', fontSize: 15 }}>
          Sports performance
        </Typography>
        <Typography variant="body2" sx={{ color: brandColors.slate, fontWeight: 600, mt: 0.75 }}>
          Foundation scores, skill journeys, and across-sports results appear here after contest submissions.
        </Typography>
      </Box>
    );
  }

  return (
    <Stack spacing={1.5}>
      {scoreRows.length ? (
        <Box sx={{ ...railCard, px: 1.5, py: 1.25 }}>
          <Typography sx={{ fontWeight: 800, color: brandColors.navy, fontFamily: '"Sora", Arial, sans-serif', fontSize: 15 }}>
            Across sports
          </Typography>
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fill, minmax(140px, 1fr))',
              gap: 0.75,
              mt: 1,
            }}
          >
            {scoreRows.map((row) => (
              <Box key={row.sport} sx={{ border: '1px solid #eef2f7', borderRadius: 1, px: 1.1, py: 0.9 }}>
                <Typography sx={{ fontSize: 11, fontWeight: 800, color: brandColors.slate }}>{row.sport}</Typography>
                <Typography sx={{ fontWeight: 900, color: brandColors.navy, fontSize: 18, lineHeight: 1.2, mt: 0.25 }}>
                  {row.avgScore != null ? Number(row.avgScore).toFixed(1) : '—'}
                </Typography>
                <Typography sx={{ fontSize: 11, fontWeight: 700, color: brandColors.slate }}>
                  Avg · {row.submissionCount || 0} skills
                  {row.improvement != null
                    ? ` · ${row.improvement >= 0 ? '+' : ''}${Number(row.improvement).toFixed(1)}`
                    : ''}
                </Typography>
              </Box>
            ))}
          </Box>
          {trend.length ? (
            <Stack direction="row" spacing={0.75} sx={{ mt: 1.15 }} alignItems="flex-end">
              {trend.map((quarter) => (
                <Box key={quarter.key} sx={{ flex: 1, minWidth: 0, textAlign: 'center' }}>
                  <Typography sx={{ fontWeight: 800, fontSize: 13, color: quarter.avgScore != null ? brandColors.navy : '#94a3b8' }}>
                    {quarter.avgScore != null ? Number(quarter.avgScore).toFixed(0) : '–'}
                  </Typography>
                  <Typography sx={{ fontSize: 10, fontWeight: 800, color: brandColors.slate }}>
                    {quarter.label.replace(/\s\d{4}$/, '')}
                  </Typography>
                </Box>
              ))}
            </Stack>
          ) : null}
        </Box>
      ) : null}

      {foundations.map((block, index) => (
        <FoundationScoutPanel
          key={`${block.sport}-${block.ageCategory}-${index}`}
          foundation={block}
          player={player}
        />
      ))}

      {journeyGroups.map((group) => (
        <Box key={group.sport}>
          <Typography sx={{ fontWeight: 800, color: brandColors.navy, fontFamily: '"Sora", Arial, sans-serif', fontSize: 14, mb: 0.85 }}>
            {group.sport} journeys
          </Typography>
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' },
              gap: 0.85,
            }}
          >
            {group.items.map((journey) => (
              <SkillQuarterJourney key={`${group.sport}-${journey.skillName}`} journey={journey} />
            ))}
          </Box>
        </Box>
      ))}

      {recentSkills?.length ? (
        <Box sx={{ ...railCard, px: 1.5, py: 1.2 }}>
          <Typography sx={{ fontWeight: 800, color: brandColors.navy, fontFamily: '"Sora", Arial, sans-serif', fontSize: 14, mb: 0.75 }}>
            Latest skill scores
          </Typography>
          <Stack spacing={0.45}>
            {recentSkills.slice(0, 10).map((skill, index) => (
              <Stack
                key={`${skill.skillName}-${index}`}
                direction="row"
                justifyContent="space-between"
                spacing={1}
                sx={{ py: 0.25 }}
              >
                <Typography sx={{ fontWeight: 700, fontSize: 13, color: brandColors.navy }}>
                  {skill.skillName || 'Skill'}
                </Typography>
                <Typography sx={{ fontWeight: 800, fontSize: 13, color: brandColors.blue, whiteSpace: 'nowrap' }}>
                  {skill.score != null ? Number(skill.score).toFixed(1) : '—'}
                </Typography>
              </Stack>
            ))}
          </Stack>
        </Box>
      ) : null}
    </Stack>
  );
};

export default PublicSportsPerformancePanel;
